import React from 'react';
import { FormModal, FormModalAction } from './FormModal';
import { CycleOrder } from '../../api/singlePositionCycles';
import './OrderDetailModal.css';

/**
 * Reusable single-order detail view (spec REQ-012). The canonical surface for viewing one Coinbase
 * trade order — leg role, aggregated exchange execution, and order parameters — reachable from the
 * cycle drill-down and reusable elsewhere. Read-only: for an OPEN order the action area offers a
 * "manage on exchange" external link; NO in-app mutation. The action array is the seam a future
 * in-app action (cancel/edit) attaches to without reworking the drill-down.
 */

const COINBASE_ADVANCED_TRADE_URL = 'https://www.coinbase.com/advanced-trade/spot';

interface Props {
  order: CycleOrder;
  onClose: () => void;
}

function fmtNum(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function fmtStr(value: string | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : value;
}

function fmtBool(value: boolean | null | undefined): string {
  return value === null || value === undefined ? '—' : value ? 'Yes' : 'No';
}

function fmtTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/** Short human-readable stop direction, folded inline onto the Stop Price value (REQ-015). */
function stopDirectionShort(value: string | null | undefined): string {
  if (value === 'STOP_DIRECTION_STOP_DOWN') return 'Stop-loss';
  if (value === 'STOP_DIRECTION_STOP_UP') return 'Take-profit';
  return value ?? '';
}

/** Stop Price display: the price, with the direction folded in when present. */
function stopPriceDisplay(order: CycleOrder): string {
  if (order.stopPrice === null || order.stopPrice === undefined) return '—';
  const dir = order.stopDirection ? ` · ${stopDirectionShort(order.stopDirection)}` : '';
  return `${fmtNum(order.stopPrice)}${dir}`;
}

const ROLE_LABELS: Record<string, string> = {
  ENTRY: 'Entry',
  PARTIAL_EXIT: 'Partial Exit',
  SECOND_PARTIAL_EXIT: 'Second Partial Exit',
  EXIT: 'Exit',
  PRIMARY_STOP: 'Primary Stop',
  SECONDARY_STOP: 'Secondary Stop',
  TERTIARY_STOP: 'Tertiary Stop',
  PENDING_STOP: 'Pending Stop',
};

const OrderDetailModal: React.FC<Props> = ({ order, onClose }) => {
  const isOpen = order.status === 'OPEN';

  const actions: FormModalAction[] = [];
  if (isOpen) {
    // Read-only for now: send the operator to the exchange to act on a live order (REQ-012).
    // A future in-app cancel/edit action attaches here without reworking this component.
    actions.push({
      label: 'Manage on Coinbase ↗',
      variant: 'secondary',
      onClick: () => window.open(COINBASE_ADVANCED_TRADE_URL, '_blank', 'noopener,noreferrer'),
    });
  }
  actions.push({ label: 'Close', variant: 'secondary', onClick: onClose });

  const roleLabel = ROLE_LABELS[order.role] ?? order.role;

  return (
    <FormModal title={`${roleLabel} Order`} onClose={onClose} actions={actions} size="wide" contentClassName="order-detail-modal">
      <div className="order-detail-grid">
        {/* Role is the modal title — omit it here so Status/Side and the two IDs pair cleanly. */}
        <Field label="Status" value={fmtStr(order.status)} />
        <Field label="Side" value={fmtStr(order.side)} />
        <Field label="Client Order ID" value={fmtStr(order.clientOrderId)} mono />
        <Field label="Exchange Order ID" value={fmtStr(order.orderId)} mono />

        <SectionHeading>Exchange Execution</SectionHeading>
        <Field label="Filled Size" value={fmtNum(order.filledSize, 8)} />
        <Field label="Avg Fill Price" value={fmtNum(order.averageFillPrice)} />
        <Field label="Filled Value" value={fmtNum(order.filledValue)} />
        <Field label="Total Fees" value={fmtNum(order.totalFees)} />
        <Field label="Value After Fees" value={fmtNum(order.valueAfterFees)} />
        <Field label="Fill Time" value={fmtTime(order.fillTime)} />

        <SectionHeading>Order Parameters</SectionHeading>
        <Field label="Quote Size" value={fmtNum(order.quoteSize)} />
        <Field label="Base Size" value={fmtNum(order.baseSize, 8)} />
        <Field label="Limit Price" value={fmtNum(order.limitPrice)} />
        <Field label="Stop Price" value={stopPriceDisplay(order)} />
        <Field label="Post Only" value={fmtBool(order.postOnly)} />
        <Field label="Completion %" value={fmtNum(order.completionPercentage)} />
      </div>
    </FormModal>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="order-detail-field">
    <span className="order-detail-label">{label}</span>
    <span className={`order-detail-value ${mono ? 'order-detail-value--mono' : ''}`}>{value}</span>
  </div>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="order-detail-heading">{children}</div>
);

export default OrderDetailModal;
